import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Modal, View, Text, StyleSheet } from 'react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY, ELEVATION } from '../../theme';
import { Button } from './Button';
import { useEscapeLayer } from '../../utils/escapeStack';

export interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

interface ConfirmDialogProps extends ConfirmOptions {
  visible: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Cross-platform confirmation dialog. Centered overlay with a semi-transparent
 * backdrop; backdrop tap or Cancel dismisses. Built on RN `Modal`, which is
 * the appropriate primitive for a blocking confirm (bottom sheets are reserved
 * for content panels per project preference).
 *
 * Prefer the `useConfirm()` hook below to invoke this imperatively.
 */
function ConfirmDialog({
  visible,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  onCancel,
  onConfirm,
}: ConfirmDialogProps) {
  const { colors } = useTheme();
  // RN Modal handles Escape itself (onRequestClose); this only tells page
  // Escape handlers underneath that a dialog is up.
  useEscapeLayer(visible);

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onCancel}>
      <View
        style={styles.backdrop}
        onStartShouldSetResponder={() => true}
        onResponderRelease={onCancel}
        accessibilityLabel="Close dialog">
        {/* Swallow presses on the card so the backdrop only dismisses from
            outside the card. */}
        <View
          onStartShouldSetResponder={() => true}
          onResponderRelease={(e) => e.stopPropagation()}
          style={styles.cardWrap}>
          <View
            style={[
              styles.card,
              ELEVATION.lg,
              { backgroundColor: colors.background, borderColor: colors.border },
            ]}>
            <Text style={[styles.title, { color: colors.text }]}>{title}</Text>
            {message && (
              <Text style={[styles.message, { color: colors.textSecondary }]}>{message}</Text>
            )}
            <View style={styles.actions}>
              <Button
                title={cancelLabel}
                onPress={onCancel}
                variant="ghost"
                size="md"
                style={styles.action}
              />
              <Button
                title={confirmLabel}
                onPress={onConfirm}
                variant={destructive ? 'danger' : 'primary'}
                size="md"
                style={styles.action}
              />
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
}

// --- appAlert web host ---
//
// RN-web's Alert.alert is a no-op, so every Alert.alert call site was
// silently doing nothing on web (src/utils/appAlert.ts is the fix — see its
// header comment). appAlert needs to work from anywhere: a hook, a store
// callback, code that isn't necessarily rendering under a live React tree at
// the moment it's called. useConfirm()'s context can't do that (and throws
// outright inside a gorhom sheet's portaled children, above ConfirmProvider
// — see MapLayersContent's doc comment), so this is a plain module-level
// queue instead, with AppAlertHost as its one subscriber. AppAlertHost is
// mounted once by ConfirmProvider at the app root, so it renders regardless
// of where the call came from.

export interface AppAlertButton {
  text: string;
  onPress?: () => void;
  style?: 'default' | 'cancel' | 'destructive';
}

interface WebAlertRequest {
  id: number;
  title: string;
  message?: string;
  buttons: AppAlertButton[];
}

let webAlertIdSeq = 0;
let webAlertQueue: WebAlertRequest[] = [];
let webAlertListener: ((request: WebAlertRequest | null) => void) | null = null;

function notifyWebAlertListener(): void {
  webAlertListener?.(webAlertQueue[0] ?? null);
}

/** Public entry point is `appAlert()` in src/utils/appAlert.ts — this is the
 *  web-only enqueue it calls. Defaults to a single "OK" button, matching
 *  Alert.alert's own behavior when no buttons are given. */
export function enqueueWebAlert(title: string, message?: string, buttons?: AppAlertButton[]): void {
  const resolvedButtons = buttons && buttons.length > 0 ? buttons : [{ text: 'OK' }];
  webAlertQueue = [
    ...webAlertQueue,
    { id: ++webAlertIdSeq, title, message, buttons: resolvedButtons },
  ];
  if (webAlertQueue.length === 1) notifyWebAlertListener();
}

function dismissCurrentWebAlert(): void {
  webAlertQueue = webAlertQueue.slice(1);
  notifyWebAlertListener();
}

function buttonVariant(button: AppAlertButton): 'primary' | 'ghost' | 'danger' {
  if (button.style === 'destructive') return 'danger';
  if (button.style === 'cancel') return 'ghost';
  return 'primary';
}

/** Renders the current queued web alert, if any. One instance, mounted by
 *  ConfirmProvider at the app root — never inside a sheet, so it's reachable
 *  from anywhere appAlert() is called. Unlike ConfirmDialog above, a scrim
 *  click never dismisses it (an alert is a blocking notice, not a
 *  cancel-by-clicking-away confirm) and the cancel-style button — when
 *  there is one — takes initial focus. */
function AppAlertHost() {
  const { colors } = useTheme();
  const [request, setRequest] = useState<WebAlertRequest | null>(null);
  const buttonRefs = useRef<(React.ElementRef<typeof Button> | null)[]>([]);

  useEffect(() => {
    webAlertListener = setRequest;
    setRequest(webAlertQueue[0] ?? null);
    return () => {
      if (webAlertListener === setRequest) webAlertListener = null;
    };
  }, []);

  useEscapeLayer(request !== null);

  const cancelIndex = request?.buttons.findIndex((b) => b.style === 'cancel') ?? -1;

  useEffect(() => {
    if (!request || cancelIndex < 0) return;
    // Deferred a tick: the Modal (and the button inside it) needs to finish
    // mounting before a DOM node exists to call .focus() on.
    const timer = setTimeout(() => {
      const node = buttonRefs.current[cancelIndex] as unknown as { focus?: () => void } | null;
      node?.focus?.();
    }, 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on request identity, not cancelIndex alone
  }, [request?.id]);

  if (!request) return null;

  const handlePress = (button: AppAlertButton) => {
    dismissCurrentWebAlert();
    button.onPress?.();
  };

  return (
    <Modal visible transparent animationType="fade" onRequestClose={() => {}}>
      <View style={styles.backdrop} accessibilityLabel={request.title}>
        <View style={styles.cardWrap}>
          <View
            style={[
              styles.card,
              ELEVATION.lg,
              { backgroundColor: colors.background, borderColor: colors.border },
            ]}>
            <Text style={[styles.title, { color: colors.text }]}>{request.title}</Text>
            {request.message && (
              <Text style={[styles.message, { color: colors.textSecondary }]}>
                {request.message}
              </Text>
            )}
            <View style={styles.actions}>
              {request.buttons.map((button, i) => (
                <Button
                  key={`${button.text}-${i}`}
                  ref={(node) => {
                    buttonRefs.current[i] = node;
                  }}
                  title={button.text}
                  onPress={() => handlePress(button)}
                  variant={buttonVariant(button)}
                  size="md"
                  style={styles.action}
                  testID={`app-alert-button-${i}`}
                />
              ))}
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
}

// --- Imperative API ---

interface ConfirmContextValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

/**
 * Provider that renders a single `ConfirmDialog` instance and exposes an
 * imperative `confirm()` returning a promise that resolves to true/false.
 * Mount once at the app root.
 */
export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
      setState(options);
    });
  }, []);

  const close = useCallback((value: boolean) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setState(null);
    resolve?.(value);
  }, []);

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      <ConfirmDialog
        visible={state !== null}
        title={state?.title ?? ''}
        message={state?.message}
        confirmLabel={state?.confirmLabel}
        cancelLabel={state?.cancelLabel}
        destructive={state?.destructive}
        onCancel={() => close(false)}
        onConfirm={() => close(true)}
      />
      <AppAlertHost />
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): (options: ConfirmOptions) => Promise<boolean> {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used inside <ConfirmProvider>');
  }
  return ctx.confirm;
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    // No theme token matches this exact alpha (colors.overlay is 0.5/0.7,
    // overlayLight is 0.3/0.5); swapping to either would visibly darken or
    // lighten the backdrop.
    // eslint-disable-next-line no-restricted-syntax -- see comment above
    backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: SPACING.md,
  },
  cardWrap: {
    width: '100%',
    maxWidth: 360,
  },
  card: {
    borderRadius: BORDER_RADIUS.lg,
    padding: SPACING.lg,
    borderWidth: StyleSheet.hairlineWidth,
  },
  title: {
    ...TYPOGRAPHY.h4,
  },
  message: {
    ...TYPOGRAPHY.body,
    marginTop: SPACING.sm,
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: SPACING.sm,
    marginTop: SPACING.lg,
  },
  action: {
    minWidth: 96,
  },
});
